'use client'

import { debounce } from 'lodash'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'

import { Stamp } from '@src/components/TopNavV5/Stamp'
import { useTopNavV5 } from '@src/components/TopNavV5/TopNavV5Context'
import type { TopNavV5MenuItem } from '@src/components/TopNavV5/TopNavV5Props'
import { cn } from '@src/utils/tailwind'

export const TopNavV5DesktopMiniMenu = (props: {
  item: TopNavV5MenuItem
  componentKey: string
  index: number
  itemClasses: string
}) => {
  const { item, componentKey, index, itemClasses } = props

  const { setExpanded, topNavDesktopTopOffset } = useTopNavV5()
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const [triggerX, setTriggerX] = useState<number>(0)

  useEffect(() => {
    const handleResize = () => {
      if (triggerRef.current) {
        setTriggerX(
          triggerRef.current.getBoundingClientRect().x + triggerRef.current.offsetWidth / 2
        )
      }
    }

    const debouncedHandleResize = debounce(handleResize, 200)

    handleResize()

    window.addEventListener('resize', debouncedHandleResize)
    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
    }
  }, [])

  return (
    <div
      key={componentKey}
      className={cn('group relative flex h-full items-center transition-all hover:text-bodi-white')}
      onMouseEnter={() => {
        setActiveIndex(index)
        setExpanded(false)
      }}
      onMouseLeave={() => {
        setActiveIndex(null)
      }}
    >
      <div
        ref={triggerRef}
        className={cn(itemClasses, 'relative flex items-center gap-2 font-normal')}
      >
        {item.title}
        <Stamp text={item.stamp} />
        <div
          className={
            'absolute bottom-0 left-0 z-max h-[3px] w-full bg-bodi-green-bright-500 opacity-0 transition-all duration-200 group-hover:opacity-100'
          }
        />
      </div>

      <div
        className={cn(
          'fixed z-30 -translate-x-1/2 bg-bodi-black-text bg-linear-to-t from-white/10 to-white/0 py-5 whitespace-nowrap transition-all duration-200',
          'invisible hidden',
          {
            'visible block': activeIndex === index,
          }
        )}
        style={{
          left: triggerX,
          top: topNavDesktopTopOffset,
        }}
      >
        <ul>
          {item.miniMenuItems?.map((subItem, index) => (
            <li className="flex flex-col px-6 py-2" key={`${subItem.title}-${index}`}>
              <div className="flex items-center">
                <Link
                  href={subItem.href ?? ''}
                  className="text-body-base whitespace-nowrap text-bodi-gray-butters hover:text-bodi-white hover:underline"
                  dangerouslySetInnerHTML={{ __html: subItem.title ?? '' }}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
